import { ArrowLeft, MailPlus, X } from 'lucide-react'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { toast } from 'sonner'
import { PageTitle } from '@/app/layout'
import { Chip } from '@/components/redesign/bits'
import { Segmented } from '@/components/ui/segmented'
import { Skeleton } from '@/components/ui/skeleton'
import { EmptyState, ErrorState } from '@/components/ui/states'
import { errorMessage } from '@/lib/api/errors'
import type { SupportEmail } from '@/lib/api/types'
import { formatRelative } from '@/lib/format'
import { type HeldFilter, useDismissEmail, useMakeTicket, useSupportEmails } from './api'

const statusChip: Record<string, { label: string; tone: 'bad' | 'warn' | 'line' | '' }> = {
  held: { label: 'Held', tone: 'bad' },
  skipped: { label: 'Automatic', tone: 'line' },
  failed: { label: 'Couldn’t read', tone: 'warn' },
  dismissed: { label: 'Dismissed', tone: '' },
}

/**
 * Emails to support@ that didn't become tickets: spam and viruses Amazon SES caught, automatic
 * mail (auto-replies, bounces, newsletters) and any that couldn't be read. Make a ticket from
 * one that a person really sent, or dismiss it. Amazon keeps the emails for 30 days.
 */
export function HeldEmailsPage() {
  const [status, setStatus] = useState<HeldFilter>('all')
  const [page, setPage] = useState(1)
  const emails = useSupportEmails(status, page)
  const make = useMakeTicket()
  const dismiss = useDismissEmail()
  const navigate = useNavigate()
  const [open, setOpen] = useState<string | null>(null)

  const makeTicket = (e: SupportEmail) =>
    make.mutate(e.id, {
      onSuccess: (done) => {
        toast.success('Ticket made.')
        if (done.ticket) void navigate(`/tickets/${done.ticket}`)
      },
      onError: (err) => toast.error(errorMessage(err)),
    })

  return (
    <>
      <PageTitle
        title="Held emails"
        description="Emails to support@ that didn’t become tickets: spam or viruses Amazon SES caught, automatic mail such as out-of-office replies and bounces, and any that couldn’t be read. If a person really wrote one, make it a ticket. Amazon keeps them for 30 days."
        actions={
          <Link to="/tickets" className="btn">
            <ArrowLeft aria-hidden="true" />
            Support inbox
          </Link>
        }
      />
      <div className="fbar">
        <Segmented
          label="Show"
          value={status}
          onChange={(v) => {
            setStatus(v)
            setPage(1)
          }}
          options={[
            { value: 'all', label: 'Waiting' },
            { value: 'held', label: 'Spam and viruses' },
            { value: 'skipped', label: 'Automatic' },
            { value: 'failed', label: 'Couldn’t read' },
            { value: 'dismissed', label: 'Dismissed' },
          ]}
        />
      </div>
      {emails.isPending ? (
        <Skeleton className="h-64 rounded-[14px]" />
      ) : emails.isError ? (
        <div className="card">
          <ErrorState error={emails.error} onRetry={() => void emails.refetch()} />
        </div>
      ) : emails.data.data.length === 0 ? (
        <div className="card">
          <EmptyState
            title="Nothing held"
            description="Every email to support@ became a ticket, or a reply on one."
          />
        </div>
      ) : (
        <div className="tw">
          <table className="t" style={{ minWidth: 760 }}>
            <caption className="sr-only">Held emails</caption>
            <thead>
              <tr>
                <th>From</th>
                <th>Subject</th>
                <th>Why</th>
                <th>Received</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {emails.data.data.map((e) => {
                const chip = statusChip[e.status] ?? { label: e.status, tone: '' as const }
                return (
                  <tr key={e.id}>
                    <td style={{ maxWidth: 220 }}>
                      <span className="break-all">{e.from ?? 'No sender'}</span>
                    </td>
                    <td style={{ maxWidth: 320 }}>
                      <button
                        type="button"
                        className="text-left font-semibold text-ink hover:underline"
                        aria-expanded={open === e.id}
                        onClick={() => setOpen(open === e.id ? null : e.id)}
                      >
                        {e.subject || '(no subject)'}
                      </button>
                      {open === e.id && e.text && (
                        <pre className="relative mt-2 max-h-64 overflow-auto rounded-md border border-border p-2 font-sans text-xs whitespace-pre-wrap text-ink-muted">
                          {e.text}
                        </pre>
                      )}
                    </td>
                    <td>
                      <Chip tone={chip.tone}>{chip.label}</Chip>
                      {e.reason && <small style={{ whiteSpace: 'normal' }}>{e.reason}</small>}
                    </td>
                    <td className="m" style={{ whiteSpace: 'nowrap' }}>
                      {formatRelative(e.received_at)}
                    </td>
                    <td className="r" style={{ whiteSpace: 'nowrap' }}>
                      <button
                        type="button"
                        className="btn sm"
                        disabled={make.isPending}
                        onClick={() => makeTicket(e)}
                      >
                        <MailPlus aria-hidden="true" /> Make a ticket
                      </button>{' '}
                      {e.status !== 'dismissed' && (
                        <button
                          type="button"
                          className="btn sm ghost"
                          aria-label={`Dismiss ${e.subject || 'this email'}`}
                          disabled={dismiss.isPending}
                          onClick={() =>
                            dismiss.mutate(e.id, {
                              onSuccess: () => toast.success('Dismissed.'),
                              onError: (err) => toast.error(errorMessage(err)),
                            })
                          }
                        >
                          <X aria-hidden="true" /> Dismiss
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          {emails.data.meta.last_page > 1 && (
            <div className="t-foot">
              <span>
                Page {emails.data.meta.current_page} of {emails.data.meta.last_page}
              </span>
              <span className="acts">
                <button
                  type="button"
                  className="btn sm ghost"
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                >
                  Newer
                </button>
                <button
                  type="button"
                  className="btn sm ghost"
                  disabled={page >= emails.data.meta.last_page}
                  onClick={() => setPage(page + 1)}
                >
                  Older
                </button>
              </span>
            </div>
          )}
        </div>
      )}
    </>
  )
}
